import React, { useState } from 'react';
import { useIntl } from 'umi';
import { Table, TableAddon, TableColumn } from 'tea-component';
import Abnormal from '@home/components/Abnormal';
import LineChart from '@intelligence/components/Chart/components/LineChart';
import CardSlot from '@gp/components/CardSlot';
import MultipleLinesTextBubble from '@gp/components/MultipleLinesTextBubble';
import { FIELDS1, FIELD_NAMES } from '@gp/utils/metricTips';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';

import './index.scss';

type ITabType = 'column-chart' | 'discount-chart' | 'sheet';

interface ITrendsCardData<T extends IRecord = IRecord<any>> {
  isLoading: boolean;
  isError: boolean;
  isNoData: boolean;
  title: string;
  titleTipsCfg: IRecord<string | React.ReactElement | null>;
  addons?: TableAddon<T>[];
  columns: TableColumn<T>[];
  records: T[];
  barOption: any;
  lineOption: any;
  downloadLoading?: boolean;
  onDownload: (e?: React.SyntheticEvent) => void;
}

interface ITrendsCardProps<T extends IRecord = IRecord<any>> extends IBaseProps, ITrendsCardData<T> {}

// 右边图标
const ICONS: {
  icon: ITabType;
  context: string;
}[] = [
  {
    icon: 'sheet',
    context: 'Text table',
  },
  {
    icon: 'discount-chart',
    context: 'Line chart',
  },
  {
    icon: 'column-chart',
    context: 'Bar chart',
  },
];

const TrendsCard: React.FC<ITrendsCardProps> = (props) => {
  const {
    isLoading = true,
    isError = false,
    isNoData = false,
    title,
    titleTipsCfg,
    addons,
    columns,
    records,
    barOption,
    lineOption,
    downloadLoading,
    onDownload,
  } = props || {};

  // 中英切换方法
  const { formatMessage } = useIntl();

  const [activeTab, setActiveTab] = useState<ITabType>('column-chart');

  const chartMap: Record<ITabType, { content: string; component: React.ReactElement }> = {
    'column-chart': {
      content: formatMessage({ id: 'dbg_common_bar_chart' }),
      component: <LineChart key="dbg-projectdetails-finance-dev-cost" height={350} lineOption={barOption} />,
    },
    'discount-chart': {
      content: formatMessage({ id: 'dbg_common_line_chart' }),
      component: <LineChart key="dbg-projectdetails-finance-headcount" height={350} lineOption={lineOption} />,
    },
    sheet: {
      content: formatMessage({ id: 'dbg_common_table' }),
      component: (
        <Table
          className="dbg-trends-card-dateils-table"
          recordKey="type"
          addons={addons}
          columns={columns}
          records={records}
        />
      ),
    },
  };

  return (
    <CardSlot
      className="dbg-trends-card-dateils"
      isDownload
      downloadLoading={downloadLoading}
      title={
        <MultipleLinesTextBubble fields={FIELDS1} names={FIELD_NAMES} data={titleTipsCfg}>
          <span>{title}</span>
        </MultipleLinesTextBubble>
      }
      iconList={ICONS}
      activeTab={activeTab}
      getChange={setActiveTab}
      onDownload={onDownload}
    >
      <Abnormal
        style={{ height: 340 }}
        loadingMsg="Loading..."
        nodataMsg="No data under current filter selections."
        isLoading={isLoading}
        isError={isError}
        isNoData={isNoData}
        component={chartMap[activeTab]?.component}
      />
    </CardSlot>
  );
};

export default TrendsCard;
